/**
 * Tariffs page: template MUI components + API.
 * Table of tariff groups (GetAllGroup); row click opens detail drawer.
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import Stack from '@mui/material/Stack';
import Chip from '@mui/material/Chip';
import Alert from '@mui/material/Alert';
import { DataGrid, gridClasses } from '@mui/x-data-grid';
import RefreshIcon from '@mui/icons-material/Refresh';
import * as api from '../../modules/api';
import PageContainer from '../mui-crud/components/PageContainer';
import TariffDetailDrawer from './TariffDetailDrawer';
import DataGridTableToolbar from '../components/DataGridTableToolbar';
import useDataGridLocaleText from '../hooks/useDataGridLocaleText';
import { getCurrencySymbolForLocation } from '../utils/currency';
import { findTariffByCrossLinkQuery } from '../utils/rentDisplay';

const CROSS_LINK_TARIFF_KEY = 'crossLinkTariffName';

function normalizeTariffList(data) {
  if (Array.isArray(data)) return data;
  if (data && typeof data === 'object') return Object.values(data);
  return [];
}

function mapTariffsToRows(list) {
  if (!Array.isArray(list)) return [];
  return list.map((tg, index) => {
    const id = tg.id ?? tg.name ?? `row-${index}`;
    const currency = getCurrencySymbolForLocation(tg.location);
    const holdDisplay = tg.holdAmount != null ? `${tg.holdAmount} ${currency}` : '—';
    const penaltyDisplay = tg.penaltyAmount != null ? `${tg.penaltyAmount} ${currency}` : '—';
    return {
      id: String(id),
      name: tg.name ?? '—',
      location: tg.location ?? '—',
      tariffType: tg.tariffType ?? '—',
      holdDisplay,
      penaltyDisplay,
      partsCount: Array.isArray(tg.parts) ? tg.parts.length : 0,
      _raw: tg,
    };
  });
}

export default function TariffsPage() {
  const { t } = useTranslation();
  const dataGridLocaleText = useDataGridLocaleText();
  const [rows, setRows] = useState([]);
  const [tariffsMap, setTariffsMap] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedTariff, setSelectedTariff] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [searchInputValue, setSearchInputValue] = useState('');

  const applyCrossLink = useCallback((list) => {
    let query = null;
    try {
      query = sessionStorage.getItem(CROSS_LINK_TARIFF_KEY);
      sessionStorage.removeItem(CROSS_LINK_TARIFF_KEY);
    } catch (_) {
      query = null;
    }
    if (!query) return;
    const found = findTariffByCrossLinkQuery(list, query);
    if (found) {
      setSelectedTariff(found);
      setDetailOpen(true);
      setSearchInputValue(String(found.name || query));
      setRows(mapTariffsToRows([found]));
    } else {
      setSearchInputValue(String(query));
      setError(t('tariffs.errorNotFound'));
    }
  }, [t]);

  const loadTariffs = useCallback(async () => {
    setError(null);
    setLoading(true);
    try {
      const res = await api.GetAllGroup();
      const data = res?.data;
      const list = normalizeTariffList(data);
      setRows(mapTariffsToRows(list));
      const map = {};
      list.forEach((tg) => {
        const key = tg.id ?? tg.name;
        if (key) map[String(key)] = tg;
      });
      setTariffsMap(map);
      applyCrossLink(list);
    } catch (e) {
      setError(e?.message || t('tariffs.errorLoad'));
      setRows([]);
      setTariffsMap({});
    } finally {
      setLoading(false);
    }
  }, [applyCrossLink, t]);

  useEffect(() => {
    loadTariffs();
  }, [loadTariffs]);

  const handleRefresh = useCallback(() => {
    if (!loading) loadTariffs();
  }, [loading, loadTariffs]);

  const handleSearch = useCallback(
    (query) => {
      const q = (query ?? searchInputValue ?? '').toString().trim().toLowerCase();
      if (!q) {
        setSearchInputValue('');
        loadTariffs();
        return;
      }
      setSearchInputValue(q);
      setRows((prev) =>
        prev.filter(
          (r) =>
            (r.name && String(r.name).toLowerCase().includes(q)) ||
            (r.location && String(r.location).toLowerCase().includes(q)) ||
            (r.id && String(r.id).toLowerCase().includes(q)),
        ),
      );
    },
    [searchInputValue, loadTariffs],
  );

  const handleRowClick = useCallback(
    (params) => {
      const full = tariffsMap[params.row.id] || params.row._raw || params.row;
      setSelectedTariff(full);
      setDetailOpen(true);
    },
    [tariffsMap],
  );

  const columns = React.useMemo(
    () => [
      { field: 'id', headerName: t('tariffs.columnId'), flex: 0.4, minWidth: 80 },
      { field: 'name', headerName: t('tariffs.columnName'), flex: 1, minWidth: 140 },
      { field: 'location', headerName: t('tariffs.columnLocation'), flex: 0.5, minWidth: 80 },
      { field: 'tariffType', headerName: t('tariffs.columnType'), flex: 0.5, minWidth: 90 },
      { field: 'holdDisplay', headerName: t('tariffs.columnHold'), flex: 0.5, minWidth: 90 },
      { field: 'penaltyDisplay', headerName: t('tariffs.columnPenalty'), flex: 0.5, minWidth: 90 },
      { field: 'partsCount', headerName: t('tariffs.columnPeriods'), flex: 0.3, minWidth: 70 },
    ],
    [t],
  );

  return (
    <Box sx={{ width: '100%' }}>
      <PageContainer
        title={t('tariffs.title')}
        breadcrumbs={[]}
        actions={
          <Stack direction="row" alignItems="center" spacing={1}>
            {/* Create disabled: BE addGroupTariffs / addTariff endpoints missing (404). */}
            <Tooltip title={t('common.reloadData')} placement="right" enterDelay={1000}>
              <div>
                <IconButton size="small" aria-label="refresh" onClick={handleRefresh}>
                  <RefreshIcon />
                </IconButton>
              </div>
            </Tooltip>
          </Stack>
        }
      >
        <Box sx={{ flex: 1, width: '100%' }}>
          {error ? (
            <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>
          ) : (
            <DataGrid
              rows={rows}
              columns={columns}
              loading={loading}
              rowCount={rows.length}
              paginationMode="client"
              pageSizeOptions={[10, 25, 50]}
              onRowClick={handleRowClick}
              localeText={dataGridLocaleText}
              getRowClassName={(params) =>
                params.indexRelativeToCurrentPage % 2 === 0 ? 'even' : 'odd'
              }
              initialState={{ pagination: { paginationModel: { pageSize: 25 } } }}
              disableColumnResize
              density="compact"
              showToolbar
              slots={{ toolbar: DataGridTableToolbar }}
              slotProps={{
                toolbar: {
                  searchValue: searchInputValue,
                  onSearchChange: setSearchInputValue,
                  onSearchSubmit: handleSearch,
                  searchPlaceholder: t('tariffs.searchPlaceholder'),
                  loading,
                },
                loadingOverlay: { variant: 'circular-progress', noRowsVariant: 'circular-progress' },
                baseIconButton: { size: 'small' },
              }}
              sx={{
                [`& .${gridClasses.columnHeader}, & .${gridClasses.cell}`]: { outline: 'transparent' },
                [`& .${gridClasses.columnHeader}:focus-within, & .${gridClasses.cell}:focus-within`]: { outline: 'none' },
                [`& .${gridClasses.row}`]: { cursor: 'pointer' },
              }}
            />
          )}
        </Box>
      </PageContainer>
      <TariffDetailDrawer
        tariff={selectedTariff}
        open={detailOpen}
        onClose={() => {
          setDetailOpen(false);
          setSelectedTariff(null);
        }}
      />
    </Box>
  );
}
